import { ReactNode } from 'react';
import { P2GBox, SvgAccount, SvgEmail, SvgHelpCentre, SvgParcel, SvgPound } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '../../../lib/consts/RoutingConstants';
import { routesOfSupport } from '~/routes/groups/support';
import { Tagged } from '../../Tagged';
import { SuperLink } from './link';

export interface props {
  currentPageRoute: string;
  logEvent: (link: string, close?: boolean) => void;
}

interface ConsumerNavItem {
  target: string; // route the item navigates to
  event: string;
  translationKey: string;
  defaultLabel: string;
  icon: ReactNode;
  isActive: (current: string) => boolean;
}

// Routes that make each multi-route area "active" in the sidebar.
const orderRoutes = [RoutingConstants.OrderHistory, RoutingConstants.InTransit];
// StoredCards and AutoTopUp are listed unconditionally rather than behind their feature flags: this
// only decides which sidebar item is highlighted for the route you are ON, and you cannot be on a
// route the flag did not register. Gating it as well would just be a second place to forget.
const accountRoutes = [
  RoutingConstants.MyDetails,
  RoutingConstants.SavedAddresses,
  RoutingConstants.Billing,
  RoutingConstants.StoredCards
];
const prepayRoutes = [RoutingConstants.PrePay, RoutingConstants.AutoTopUp];

// The consumer My Account sidebar (CXA brief IA). Flat list, no SmartSend sections.
const consumerNavItems: ConsumerNavItem[] = [
  {
    target: RoutingConstants.OrderHistory,
    event: 'my-orders',
    translationKey: 'CONSUMER_NAV_MY_ORDERS_LINK',
    defaultLabel: 'My Orders',
    icon: <SvgParcel />,
    isActive: (c) => orderRoutes.includes(c)
  },
  {
    target: RoutingConstants.Support,
    event: 'support',
    translationKey: 'CONSUMER_NAV_SUPPORT_LINK',
    defaultLabel: 'Support',
    icon: <SvgHelpCentre />,
    isActive: (c) => routesOfSupport.includes(c)
  },
  {
    target: RoutingConstants.Communications,
    event: 'communications',
    translationKey: 'CONSUMER_NAV_COMMUNICATIONS_LINK',
    defaultLabel: 'Communications',
    icon: <SvgEmail />,
    isActive: (c) => c === RoutingConstants.Communications
  },
  {
    target: RoutingConstants.PrePay,
    event: 'prepay',
    translationKey: 'CONSUMER_NAV_PREPAY_LINK',
    defaultLabel: 'PrePay & Balance',
    icon: <SvgPound />,
    isActive: (c) => prepayRoutes.includes(c)
  },
  {
    target: RoutingConstants.MyDetails,
    event: 'account',
    translationKey: 'CONSUMER_NAV_ACCOUNT_LINK',
    defaultLabel: 'My Account',
    icon: <SvgAccount />,
    isActive: (c) => accountRoutes.includes(c)
  }
];

const ActionMenuContentConsumer: React.FC<props> = ({ logEvent, currentPageRoute }) => {
  const { t } = useTranslation(TranslationNamespaces.MYACCOUNT);
  const { goTo } = useGoto();

  return (
    <Tagged tag="panel-quick-actions-consumer">
      <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
        {consumerNavItems.map((item) => (
          <SuperLink
            key={item.target}
            onClick={() => {
              logEvent(item.event);
              goTo(item.target);
            }}
            isSelected={item.isActive(currentPageRoute)}
            label={t(item.translationKey, { defaultValue: item.defaultLabel })}
            icon={item.icon}
          />
        ))}
      </P2GBox>
    </Tagged>
  );
};

export default ActionMenuContentConsumer;
